import { FC, useEffect } from "react";

import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import {
  Checkbox,
  FormFooter,
  Loader,
  PageHeadeline,
  Paragraph,
} from "../../components";
import { useFetch } from "../../hooks";
import { RadioButtonSection } from "./components/RadioButtonSection";

interface IAuthFlowForm {
  enabled?: string;
  lastUsedProviderEnabled?: boolean;
}

export const AuthFlow: FC = () => {
  const { apiCall: enableTwoStepLogin, loading } = useFetch("post");
  const {
    apiCall: getTwoStepLoginStatus,
    loading: getTwoStepLoginStatusLoading,
  } = useFetch("get");
  const {
    apiCall: getLastUsedProviderStatus,
    loading: getLastUsedProviderStatusLoading,
  } = useFetch("get");
  const {
    apiCall: setLastUsedProviderEnabled,
    loading: setLastUsedProviderLoading,
  } = useFetch("post");
  const { register, handleSubmit, reset, watch, control, setValue } = useForm();

  useEffect(() => {
    getTwoStepLoginStatus(
      "Security/GetAuthenticationSetting?setting=TwoStepLoginEnabled",
      {},
      (data) => {
        reset({ enabled: data });

        // Loaded after the reset above so it cannot be wiped by it. Same generic
        // getter; a row that does not exist yet comes back empty and reads as
        // "off" - the badge is opt-in per tenant.
        getLastUsedProviderStatus(
          "Security/GetAuthenticationSetting?setting=LastUsedProviderEnabled",
          {},
          (value) => {
            setValue("lastUsedProviderEnabled", value === "True");
          }
        );
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authFlowMethod = watch("enabled");
  const twoStepLoginEnabled = authFlowMethod === "True" ? true : false;

  // The badge value arrives from a second request, so the form must stay behind the
  // loader until both have landed: a Save issued in between would read the checkbox as
  // undefined and post "off", silently disabling the badge for the whole tenant.
  const settingsLoading =
    getTwoStepLoginStatusLoading || getLastUsedProviderStatusLoading;

  const onEnablePasswordLessLogin = (data: IAuthFlowForm) => {
    const showError = (fallback: string) => (error: any) =>
      toast.error(error?.response?.data ?? fallback);

    // Chained rather than fired side by side so the success toast means "both landed".
    // Promise.all would not help: apiCall catches internally and never rethrows, so it
    // resolves even on failure - sequencing has to happen through the callbacks.
    enableTwoStepLogin(
      `Security/SedIdentifierFirstFlowEnabled?enabled=${twoStepLoginEnabled}`,
      {},
      () => {
        // Guard as well as the loader above: never write a value that never loaded.
        if (data?.lastUsedProviderEnabled === undefined) {
          toast.success("Authentication configuration successfully saved!");
          return;
        }

        setLastUsedProviderEnabled(
          `Security/SetLastUsedProviderEnabled?enabled=${data.lastUsedProviderEnabled}`,
          {},
          () => {
            toast.success("Authentication configuration successfully saved!");
          },
          showError("Failed to save the last used badge setting.")
        );
      },
      showError("Failed to save the authentication flow.")
    );
  };

  return (
    <form className="flex flex-col h-full w-full">
      <div className="flex h-full">
        <div className="w-1/2 pr-6">
          <PageHeadeline
            title="Authentication Flow"
            description="Configure how your users will authenticate when they login."
            className="mb-6"
          />
          {settingsLoading ? (
            <Loader />
          ) : (
            <div>
              <RadioButtonSection
                checked={!twoStepLoginEnabled}
                register={register("enabled")}
                value="False"
                id="plus-password"
                content={
                  <div className="pr-10">
                    <h6 className="text-base">Identifier + Password</h6>
                    <Paragraph>
                      A single login screen prompts users for their user
                      identifier and their password. This is set as the default
                      experience.
                    </Paragraph>
                  </div>
                }
              />

              <RadioButtonSection
                checked={twoStepLoginEnabled}
                register={register("enabled")}
                value="True"
                id="identifier-first"
                content={
                  <div className="pr-10">
                    <h6 className="text-base">Identifier First</h6>
                    <Paragraph>
                      An initial login screen prompts users for their user
                      identifier, then a different screen prompts users for
                      their password.
                    </Paragraph>
                  </div>
                }
              />

              <div className="mt-6 pt-6 border-t border-primary-stroke">
                <h6 className="text-base mb-2">Returning users</h6>
                <Controller
                  control={control}
                  name="lastUsedProviderEnabled"
                  render={({ field: { onChange, value, ref } }: any) => (
                    <Checkbox
                      label='Show a "Last used" badge on the provider a returning user signed in with last'
                      id="last-used-provider-enabled"
                      checked={value || false}
                      inputRef={ref}
                      onChange={onChange}
                      wrapperClassName="mb-1"
                    />
                  )}
                />
                <Paragraph>
                  Remembered per browser and per application; nothing about the
                  user is stored. Off by default.
                </Paragraph>
              </div>
            </div>
          )}
        </div>

        {!settingsLoading && (
          <div className="flex items-center justify-center w-1/2 bg-primary-stroke p-4 relative identifier-first-overlay">
            {!twoStepLoginEnabled && (
              <img
                src="/images/identifier-password.png"
                alt="Identifier Password"
              />
            )}
            {twoStepLoginEnabled && (
              <img src="/images/identifier-first.png" alt="Identifier First" />
            )}
          </div>
        )}
      </div>

      {!settingsLoading && (
        <FormFooter
          onSubmit={handleSubmit(onEnablePasswordLessLogin)}
          loading={loading || setLastUsedProviderLoading}
          mtAuto
          pb="pb-0"
          className="w-full"
        />
      )}
    </form>
  );
};
